import { leading, throttle } from "@solid-primitives/scheduled";
import type { EditorState } from "prosemirror-state";
import type { Channel } from "sdk";
import {
	createEffect,
	createMemo,
	createSignal,
	onCleanup,
	onMount,
} from "solid-js";
import { For, Match, Show, Switch } from "solid-js/web";
import { uuidv7 } from "uuidv7";
import {
	useApi,
	useChannels,
	useMessages,
	useRoomMembers,
	useUsers,
} from "@/api";
import { type Attachment, useCtx } from "@/app/context";
import icDelete from "@/assets/delete.png";
import icEdit from "@/assets/edit.png";
import cancelIc from "@/assets/x.png";
import { EmojiButton } from "@/atoms/EmojiButton.tsx";
import { Icon } from "@/atoms/Icon";
import { createTooltip } from "@/atoms/Tooltip.tsx";
import { createEditor } from "@/components/features/editor/Editor.tsx";
import { useAutocomplete } from "@/contexts/autocomplete";
import { useChannel } from "@/contexts/channel";
import { useCurrentUser } from "@/contexts/currentUser.tsx";
import { useFocusRing } from "@/contexts/focus-ring";
import { useFormattingToolbar } from "@/contexts/formatting-toolbar";
import { useModals } from "@/contexts/modal.tsx";
import { useUploads } from "@/contexts/uploads.tsx";
import { useMessageNav } from "@/hooks/useMessageNav";
import { useMessageSubmit } from "@/hooks/useMessageSubmit.ts";
import { usePermissions } from "@/hooks/usePermissions.ts";
import { getThumbFromId } from "@/media/util.tsx";
import type { MessageT, ThreadT } from "@/types";
import { getDate, getMessageOverrideName } from "@/utils/general";
import { icAdd, icUpload } from "@/utils/icons.ts";
import { isMarkdown } from "./Message";
import { useTimeline } from "./timeline-context.tsx";

type InputProps = {
	channel: Channel;
};

export function Input(props: InputProps) {
	const ctx = useCtx();
	const channels2 = useChannels();
	const messagesService = useMessages();
	const users2 = useUsers();
	const roomMembers2 = useRoomMembers();
	const store = useApi();
	const [ch, chUpdate] = useChannel()!;
	const focusRing = useFocusRing();
	const submit = useMessageSubmit(() => props.channel.id);
	const reply_id = () => ch.reply_id;
	const reply = () => messagesService.cache.get(reply_id()!);
	const uploads = useUploads();
	const currentUser = useCurrentUser();
	const timeline = useTimeline();
	const [scrollIndex, setScrollIndex] = createSignal(0);
	const [composerFocused, setComposerFocused] = createSignal(false);
	let textEl: HTMLDivElement | undefined;

	// special handling for composer keyboard nav (press tab to activate nav)
	createEffect(() => {
		focusRing.setOverride(
			ch.messageNavActive && !ch.focusedMessageId && composerFocused()
				? (textEl ?? null)
				: null,
		);
	});
	onCleanup(() => focusRing.setOverride(null));

	timeline.events.on("scrollIndex", setScrollIndex);

	// TODO: deduplicate code with frontend/src/components/features/chat/Chat.tsx
	const isAtBottom = () => {
		const si = scrollIndex();
		const m = timeline.messages;
		if (!m) return true;
		if (m.has_forward) return false; // FIXME: try to merge with live timeline, avoid gaps

		const itemsBelow = m.items.length - si;
		return itemsBelow <= 50;
	};

	function handleUpload(file: File) {
		console.log(file);
		const local_id = uuidv7();
		uploads.init(local_id, props.channel.id, file);
	}

	function uploadFile(e: InputEvent) {
		const target = e.target! as HTMLInputElement;
		const files = Array.from(target.files!);
		for (const file of files) {
			handleUpload(file);
		}
	}

	const atts = () => ch.attachments;

	const sendTyping = leading(
		throttle,
		() => {
			channels2.typing(props.channel.id);
		},
		8000,
	);

	const getName = (user_id: string) => {
		const user = users2.cache.get(user_id);
		const room_id = props.channel.room_id;
		if (!room_id) {
			return user?.name;
		}

		const member = roomMembers2.cache.get(`${room_id}:${user_id}`);
		const m = member;
		return m?.override_name ?? user?.name;
	};
	const fmt = new (Intl as any).ListFormat();

	const typingUsers = createMemo(() => {
		const user_id = currentUser()?.id;
		const user_ids = [
			...(store.typing.get(props.channel.id)?.values() ?? []),
		].filter((i) => i !== user_id);
		return user_ids;
	});

	let slowmodeRef!: HTMLDivElement;

	const slowmodeShake = () => {
		const SCALEX = 1.5;
		const SCALEY = 0.4;
		const FRAMES = 10;
		const rnd = (sx: number, sy: number) =>
			`${Math.random() * sx - sx / 2}px ${Math.random() * sy - sy / 2}px`;
		const translations = new Array(FRAMES)
			.fill(0)
			.map((_, i) => rnd(i * SCALEX, i * SCALEY))
			.reverse();
		const reduceMotion = false; // TODO
		slowmodeRef.animate(
			{
				translate: reduceMotion ? [] : translations,
				color: ["red", ""],
			},
			{ duration: 200, easing: "linear" },
		);
	};

	const onSubmit = (text: string) => {
		if (slowmodeActive()) {
			slowmodeShake();
			return false;
		}
		return submit(text, bypassSlowmode());
	};

	const onEmojiPick = (emoji: string, _keepOpen?: boolean) => {
		const editorState = ch.editor_state;
		if (editorState) {
			const { from, to } = editorState.selection;
			const customMatch = emoji.match(/^<(a?):([^:]+):([^>]+)>$/);
			let tr;
			if (customMatch) {
				const animated = customMatch[1] === "a";
				const name = customMatch[2];
				const id = customMatch[3];
				tr = editorState.tr.replaceWith(
					from,
					to,
					editor.schema.nodes.emojiCustom.create({ id, name, animated }),
				);
			} else {
				tr = editorState.tr.insertText(emoji, from, to);
			}
			const newState = editorState.apply(tr);
			chUpdate("editor_state", newState);
		}
	};

	const onChange = (state: EditorState) => {
		chUpdate("editor_state", state);
		const hasContent = state.doc.textContent.trim().length > 0;
		if (hasContent) {
			sendTyping();
		} else {
			sendTyping.clear();
		}
	};

	const toolbar = useFormattingToolbar();
	const autocomplete = useAutocomplete();
	const messageNav = useMessageNav(() => props.channel.id);

	const focusOnMessage = () => {
		const active = document.activeElement;
		return (
			active instanceof HTMLElement && !!active.closest("[data-message-id]")
		);
	};

	const isThread = () =>
		props.channel.type === "ThreadPublic" ||
		props.channel.type === "ThreadPrivate" ||
		props.channel.type === "ThreadForum2";

	const closeThreadChat = () => {
		let parent_id = props.channel.parent_id;

		// NOTE: threads currently can only be one "layer" deep, so this is currently unnecessary
		while (parent_id) {
			const cc = ctx.channel_contexts.get(parent_id);
			if (cc?.[0].thread_chat_sidebar_thread_id === props.channel.id) {
				cc[1]("thread_chat_sidebar_thread_id", undefined);
				cc[0].input_focus?.();
				return true;
			}
			parent_id = channels2.cache.get(parent_id)?.parent_id;
		}
		return false;
	};

	const editor = createEditor({
		channelId: () => props.channel.id ?? "",
		roomId: () => props.channel.room_id ?? "",
		toolbar,
		autocomplete,
		keymap: {
			Tab: () => {
				if (ch.messageNavActive) {
					chUpdate("messageNavActive", false);
					chUpdate("focusedMessageId", undefined);
					return false;
				}
				messageNav.enter();
				return true;
			},
			Escape: () => {
				if (ch.reply_id) {
					chUpdate("reply_id", undefined);
					return true;
				}
				if (ch.messageNavActive) {
					messageNav.exit();
					return true;
				}
				// jump to the newest message and mark the channel read before
				// closing a thread chat
				messageNav.jumpToEnd();
				if (isThread() && closeThreadChat()) return true;
				return true;
			},
			"Alt-Enter": () => {
				if (!ch.focusedMessageId || !focusOnMessage()) return false;
				messageNav.markUnread();
				return true;
			},
			r: () => {
				if (!ch.focusedMessageId || !focusOnMessage()) return false;
				messageNav.reply();
				return true;
			},
			ArrowUp: (state) => {
				if (ch.messageNavActive) {
					messageNav.navigate(-1);
					return true;
				}
				if (state.doc.textContent.length > 0) {
					return false; // not empty, do default behavior
				}

				const ranges = messagesService._ranges.get(props.channel.id);
				if (!ranges) return false;

				const self_id = currentUser()?.id;
				if (!self_id) return false;

				for (let i = ranges.live.items.length - 1; i >= 0; i--) {
					const msg = ranges.live.items[i];
					if (
						msg.author_id === self_id &&
						isMarkdown(msg.latest_version.type)
					) {
						chUpdate("editingMessage", {
							message_id: msg.id,
							selection: "end",
						});
						return true; // handled
					}
				}

				return false;
			},
			ArrowDown: () => {
				if (!ch.messageNavActive) return false;
				messageNav.navigate(1);
				return true;
			},
		},
	});

	onMount(() => {
		// NOTE: solid store set() calls function values as updaters, so wrap it
		chUpdate("input_focus", () => () => editor.focus());
		onCleanup(() => {
			chUpdate("input_focus", undefined);
		});
	});

	createEffect(() => {
		const state = ch.editor_state;
		editor.setState(state);
		editor.focus();
	});

	createEffect(() => {
		const rid = reply_id();
		if (rid) {
			queueMicrotask(() => editor.focus());
		}
	});

	createEffect(() => {
		const expireAt = props.channel.slowmode_message_expire_at;
		if (expireAt) {
			const currentExpireAt = ch.slowmode_expire_at;
			const newExpireAt = getDate(expireAt);
			if (
				!currentExpireAt ||
				currentExpireAt.getTime() !== newExpireAt.getTime()
			) {
				chUpdate("slowmode_expire_at", newExpireAt);
			}
		}
	});

	const perms = usePermissions(
		() => currentUser()?.id ?? "",
		() => props.channel.room_id ?? undefined,
		() => props.channel.id,
	);

	const locked = () => {
		return !!(
			!perms.has("MessageCreate") ||
			(props.channel.locked && !perms.has("ThreadManage"))
		);
	};

	const fullChannelName = (chan: Channel) => {
		const uid = currentUser()?.id;
		switch (chan.type) {
			case "Dm": {
				return `@${chan.recipients?.find((i) => i.id !== uid)?.name ?? "unknown"}`;
			}
			case "Gdm": {
				return `${
					chan.recipients
						?.filter((i) => i.id !== uid)
						?.map((i) => i.name)
						.join(", ") ?? "unknown"
				}`;
			}
			default: {
				return `#${chan.name}`;
			}
		}
	};

	const editorPlaceholder = () => {
		const name = fullChannelName(props.channel);
		if (!currentUser()) return `Log in to message ${name}`;
		if (locked()) return `You do not have permission to message ${name}`;
		return `Message ${name}`;
	};

	const bypassSlowmode = (): boolean =>
		perms.has("ChannelManage") ||
		perms.has("ThreadManage") ||
		perms.has("MemberTimeout");

	const [remainingTime, setRemainingTime] = createSignal(0);
	const slowmodeRemaining = () => remainingTime();
	const slowmodeActive = () => slowmodeRemaining() > 0;

	createEffect(() => {
		const expireAt = ch.slowmode_expire_at;
		if (expireAt) {
			const updateTimer = () => {
				const now = Date.now();
				const remaining = expireAt.getTime() - now;
				setRemainingTime(Math.max(0, remaining));
			};

			updateTimer();
			const interval = setInterval(updateTimer, 1000);
			onCleanup(() => clearInterval(interval));
		} else {
			setRemainingTime(0);
		}
	});

	const slowmodeFormatted = () => {
		const remainingMs = slowmodeRemaining();
		if (remainingMs <= 0 || bypassSlowmode()) {
			const channelSlowmode = props.channel.slowmode_message;
			if (channelSlowmode) {
				const mins = Math.floor(channelSlowmode / 60);
				const secs = channelSlowmode % 60;
				const time =
					mins === 0
						? `slowmode set to ${secs}s`
						: `slowmode set to ${mins}m${secs.toString().padStart(2, "0")}s`;
				return `slowmode set to ${time}${
					bypassSlowmode() ? " (bypassed)" : ""
				}`;
			} else return "no slowmode";
		}
		const seconds = Math.ceil(remainingMs / 1000);
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	};

	const jumpToLatest = () => {
		ch.timelineState.controller.jumpToBottom();
		// TODO: mark as read (including local marker)
	};

	const jumpToReplySource = () => {
		const source = ch.reply_jump_source;
		if (source) {
			ch.timelineState.controller.jumpToMessage(source, true);
			chUpdate("reply_jump_source", undefined);
		}
	};

	return (
		<div
			class="message-input"
			onFocusIn={() => setComposerFocused(true)}
			onFocusOut={() => setComposerFocused(false)}
			classList={{
				locked: locked() ?? false,
			}}
		>
			<Show when={atts()?.length}>
				<div class="attachments">
					<header>
						{atts()?.length}{" "}
						{atts()?.length === 1 ? "attachment" : "attachments"}
					</header>
					<ul>
						<For each={atts()}>
							{(att) => (
								<RenderUploadItem thread_id={props.channel.id} att={att} />
							)}
						</For>
					</ul>
				</div>
			</Show>
			<Switch>
				<Match when={ch.reply_jump_source}>
					<button
						type="button"
						class="jump-to-latest"
						onClick={jumpToReplySource}
					>
						you are viewing a reply &bull; click to jump to source
					</button>
				</Match>
				<Match when={!isAtBottom()}>
					<button type="button" class="jump-to-latest" onClick={jumpToLatest}>
						you are viewing older messages &bull; click to jump to present
					</button>
				</Match>
			</Switch>
			<Show when={reply()}>
				<InputReply thread={props.channel} reply={reply()!} />
			</Show>
			<div class="text" enterkeyhint="send" ref={(el) => (textEl = el)}>
				<label class="upload">
					<Icon src={icAdd} />
					<input
						multiple
						type="file"
						onInput={uploadFile}
						value="upload file"
						disabled={locked() ?? false}
					/>
				</label>
				<editor.View
					onSubmit={onSubmit}
					onChange={onChange}
					onUpload={handleUpload}
					channelId={props.channel.id}
					placeholder={editorPlaceholder()}
					disabled={locked() ?? false}
				/>
				<EmojiButton picked={onEmojiPick} />
			</div>
			<footer>
				<Show when={typingUsers().length}>
					<div class="typing">
						{fmt
							.formatToParts(typingUsers().map((i) => getName(i) || "someone"))
							.map((part) =>
								part.type === "element" ? <b>{part.value}</b> : part.value,
							)}{" "}
						{typingUsers().length === 1 ? "is" : "are"} typing
					</div>
				</Show>
				<div style="flex:1"></div>

				<Show when={props.channel.slowmode_message || slowmodeActive()}>
					{/* TODO: icon for slowmode indicator*/}
					<div class="slowmode" ref={slowmodeRef}>
						{slowmodeFormatted()}
					</div>
				</Show>
			</footer>
		</div>
	);
}

export function RenderUploadItem(props: {
	thread_id: string;
	att: Attachment;
}) {
	const uploads = useUploads();
	const [, modalCtl] = useModals();
	const thumbUrl =
		props.att.status === "uploaded"
			? getThumbFromId(props.att.media.id, 64)
			: URL.createObjectURL(props.att.file);

	if (props.att.status !== "uploaded") {
		onCleanup(() => {
			URL.revokeObjectURL(thumbUrl);
		});
	}

	function renderInfo(att: Attachment) {
		if (att.status === "uploading") {
			if (att.progress === 1) {
				return `processing`;
			} else {
				const percent = (att.progress * 100).toFixed(2);
				return `${percent}%`;
			}
		} else {
			return "";
			// return <AttachmentView media={att.media} size={64} />;
		}
	}

	function getProgress(att: Attachment) {
		if (att.status === "uploading") {
			return att.progress;
		} else {
			return 1;
		}
	}

	function removeAttachment(local_id: string) {
		uploads.cancel(local_id, props.thread_id);
	}

	function pause() {
		uploads.pause(props.att.local_id);
	}

	function resume() {
		uploads.resume(props.att.local_id);
	}

	const filename =
		props.att.status === "uploaded"
			? props.att.media.filename
			: (props.att.filename ?? props.att.file.name);

	// TODO: draw icons for these instead of using emoji

	return (
		<div class="upload-item">
			<div
				class="thumb"
				style={{ "background-image": `url(${thumbUrl})` }}
			></div>
			<div class="info" style="overflow:hidden">
				<svg
					aria-hidden="true"
					class="progress"
					viewBox="0 0 1 1"
					preserveAspectRatio="none"
				>
					<rect class="bar" height="1" width={getProgress(props.att)}></rect>
				</svg>
				<div style="display: flex">
					<div style="flex: 1;white-space:nowrap;text-overflow:ellipsis;overflow:hidden">
						{filename}
						<span style="color:#888;margin-left:.5ex">
							{renderInfo(props.att)}
						</span>
					</div>
					<menu style="display:flex">
						<Switch>
							<Match
								when={props.att.status === "uploading" && props.att.paused}
							>
								<button type="button" class="button" onClick={resume}>
									⬆️
								</button>
							</Match>
							<Match when={props.att.status === "uploading"}>
								<button type="button" class="button" onClick={pause}>
									⏸️
								</button>
							</Match>
						</Switch>
						<Show when={props.att.status === "uploaded"}>
							<button
								type="button"
								class="button"
								onClick={() =>
									modalCtl.open({
										type: "attachment",
										channel_id: props.thread_id,
										local_id: props.att.local_id,
									})
								}
							>
								<Icon src={icEdit} />
							</button>
						</Show>
						<button
							type="button"
							class="button"
							onClick={() => removeAttachment(props.att.local_id)}
						>
							<Icon src={icDelete} />
						</button>
					</menu>
				</div>
			</div>
		</div>
	);
}

const InputReply = (props: { thread: ThreadT; reply: MessageT }) => {
	const users2 = useUsers();
	const roomMembers2 = useRoomMembers();
	const tip = createTooltip({ tip: () => "remove reply" });
	const [_ch, chUpdate] = useChannel()!;
	const getName = (user_id: string) => {
		const user = users2.cache.get(user_id);
		const room_id = props.thread.room_id;
		if (!room_id) {
			return user?.name;
		}
		const member = roomMembers2.cache.get(`${room_id}:${user_id}`);

		const m = member;
		return m?.override_name ?? user?.name;
	};

	const getNameNullable = (user_id?: string) => {
		if (user_id) return getName(user_id);
	};

	return (
		<div class="reply">
			<button
				type="button"
				class="cancel"
				onClick={() => chUpdate("reply_id", undefined)}
				ref={tip.content}
			>
				<Icon src={cancelIc} />
			</button>
			<div class="info">
				replying to{" "}
				<b>
					{getMessageOverrideName(props.reply) ??
						getNameNullable(props.reply?.author_id)}
				</b>
			</div>
		</div>
	);
};
